import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReplayIcon from '@mui/icons-material/Replay';
import JsonView from '@uiw/react-json-view';

import { useJobDetails } from '../../../hooks/useJobExecutions';
import { EXECUTION_STATUS_OPTIONS } from '../../../types/taskManager';
import { format } from 'date-fns';
import { HbrDivider } from '@harbor/elements-react-standalone';

interface Props {
  open: boolean;
  jobId: string | null;
  onClose: () => void;
  onRetry: (jobId: string) => Promise<void>;
}

const JobDetailsDialog: React.FC<Props> = ({ open, jobId, onClose, onRetry }) => {
  const { job, loading } = useJobDetails(jobId);

  const getStatusColor = (status: string) => {
    const option = EXECUTION_STATUS_OPTIONS.find(opt => opt.value === status);
    return option?.color || '#9e9e9e';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm:ss');
    } catch {
      return dateString;
    }
  };

  const calculateDuration = (start?: string, end?: string) => {
    if (!start || !end) return 'N/A';
    try {
      const duration = (new Date(end).getTime() - new Date(start).getTime()) / 1000;
      return `${duration.toFixed(2)}s`;
    } catch {
      return 'N/A';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Job Details</Typography>
          {job && job.status === 'failed' && (
            <Button
              size="small"
              startIcon={<ReplayIcon />}
              onClick={() => jobId && onRetry(jobId)}
              variant="outlined"
              color="primary"
            >
              Retry Job
            </Button>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : job ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Job Info */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Job Information
              </Typography>
              <HbrDivider />

              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell width="30%">
                      <strong>Job ID:</strong>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {job.job_id}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Type:</strong>
                    </TableCell>
                    <TableCell>{job.job_type}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Status:</strong>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={job.status}
                        size="small"
                        sx={{ bgcolor: getStatusColor(job.status), color: 'white' }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Created:</strong>
                    </TableCell>
                    <TableCell>{formatDate(job.created_at)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Started:</strong>
                    </TableCell>
                    <TableCell>{formatDate(job.started_at)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Completed:</strong>
                    </TableCell>
                    <TableCell>{formatDate(job.completed_at)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Duration:</strong>
                    </TableCell>
                    <TableCell>{calculateDuration(job.started_at, job.completed_at)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Retry Count:</strong>
                    </TableCell>
                    <TableCell>{job.retry_count}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>

            {/* Error */}
            {job.error_message && (
              <Alert severity="error">
                <Typography variant="body2">
                  <strong>Error:</strong> {job.error_message}
                </Typography>
              </Alert>
            )}

            {/* Tasks */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Tasks ({job.tasks?.length || 0})
              </Typography>
              <HbrDivider />

              {job.tasks && job.tasks.length > 0 ? (
                job.tasks.map(task => (
                  <Accordion key={task.id}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography variant="body2">{task.task_id}</Typography>
                        <Chip
                          label={task.status}
                          size="small"
                          sx={{ bgcolor: getStatusColor(task.status), color: 'white' }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          ({task.task_type})
                        </Typography>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="caption">
                          <strong>Type:</strong> {task.task_type}
                        </Typography>
                        <Typography variant="caption">
                          <strong>Order:</strong> {task.task_order}
                        </Typography>
                        <Typography variant="caption">
                          <strong>Started:</strong> {formatDate(task.started_at)}
                        </Typography>
                        <Typography variant="caption">
                          <strong>Completed:</strong> {formatDate(task.completed_at)}
                        </Typography>
                        <Typography variant="caption">
                          <strong>Duration:</strong> {calculateDuration(task.started_at, task.completed_at)}
                        </Typography>

                        {task.error_message && (
                          <Alert severity="error" sx={{ mt: 1 }}>
                            <Typography variant="caption">
                              <strong>Error:</strong> {task.error_message}
                            </Typography>
                          </Alert>
                        )}

                        {task.result && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" display="block" gutterBottom>
                              <strong>Result:</strong>
                            </Typography>
                            <Box sx={{ maxHeight: 200, overflow: 'auto', fontSize: '0.7rem' }}>
                              <JsonView
                                value={task.result}
                                collapsed={1}
                                displayDataTypes={false}
                                displayObjectSize={false}
                                enableClipboard
                              />
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <Typography variant="caption">No tasks</Typography>
              )}
            </Box>

            {/* Job Result */}
            {job.result && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Job Result
                </Typography>
                <HbrDivider />
                <Box sx={{ maxHeight: 300, overflow: 'auto', fontSize: '0.7rem' }}>
                  <JsonView
                    value={job.result}
                    collapsed={1}
                    displayDataTypes={false}
                    displayObjectSize={false}
                    enableClipboard
                  />
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          <Typography>Job not found</Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default JobDetailsDialog;